import { useState, useEffect } from "react";
import { useAuthContext } from "../../hooks/useAuthContext";
import getAuthHeaders from "../../utils/getAuthHeaders";
import Loading from "../Loading";
import reportError from "../../utils/reportError";

import "./PickupTimes.css";

// Helper function to format date from MM/DD/YYYY to "Fri, Apr 24"
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options = { weekday: "short", month: "short", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

// Helper function to format date to "June 6"
const formatDateMonthDay = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options = { month: "long", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

// Helper function to calculate days remaining
const calculateDaysRemaining = (dateString) => {
  if (!dateString) return 0;
  const targetDate = new Date(dateString);
  const today = new Date();
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const PickupTimes = () => {
  const { user } = useAuthContext();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isPending, setPending] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    if (user) {
      const getLocation = async () => {
        try {
          const authHeader = await getAuthHeaders(user);

          let locationUrl = `/api/routes/`;

          if (process.env.NODE_ENV === "development") {
            locationUrl = `${process.env.REACT_APP_NETWORK}${locationUrl}`;
          }

          fetch(locationUrl, { headers: authHeader }).then(async (res) => {
            const json = await res.json();
            setData(json);
            // initialize defaults after data arrives
            try {
              const pickupTimes = json.pickupTimes || [];
              const locations = Array.from(
                new Set(pickupTimes.map((p) => p.dropLocation || p.location))
              );
              const preferred =
                user?.location && locations.includes(user.location)
                  ? user.location
                  : locations[0] || "";
              setSelectedLocation(preferred);
              if (preferred) {
                const dates = Array.from(
                  new Set(
                    pickupTimes
                      .filter(
                        (p) => (p.dropLocation || p.location) === preferred
                      )
                      .map((p) => p.date)
                  )
                );
                setSelectedDate(dates[0] || "");
              }
            } catch (e) {
              // ignore initialization errors
            }
            setPending(false);
          });
        } catch (err) {
          reportError(err, "fetch pickupTimes");
          setError(err.message);
          setPending(false);
        }
      };

      getLocation();
    }
  }, [user]);

  return (
    <div>
      {data && <h1>Pick-up Times</h1>}
      {isPending && <Loading />}

      {data && data.pickupTimes && (
        <div>
          <form className="pickup-filters">
            <label htmlFor="locationSelect">Choose a location:</label>
            <select
              id="locationSelect"
              value={selectedLocation}
              onChange={(e) => {
                const loc = e.target.value;
                setSelectedLocation(loc);
                // default to first date available for newly selected location
                if (data && data.pickupTimes) {
                  const dates = Array.from(
                    new Set(
                      data.pickupTimes
                        .filter((p) => (p.dropLocation || p.location) === loc)
                        .map((p) => p.date)
                    )
                  );
                  setSelectedDate(dates[0] || "");
                } else {
                  setSelectedDate("");
                }
              }}
            >
              {Array.from(
                new Set(
                  data.pickupTimes.map((p) => p.dropLocation || p.location)
                )
              ).map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>

            {selectedLocation && (
              <>
                <label htmlFor="dateSelect">Choose a date:</label>
                <select
                  id="dateSelect"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                >
                  {Array.from(
                    new Set(
                      data.pickupTimes
                        .filter(
                          (p) =>
                            (p.dropLocation || p.location) === selectedLocation
                        )
                        .map((p) => p.date)
                    )
                  ).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </>
            )}
          </form>
          <table className="center">
            <tbody>
              <tr key="header">
                <th>Location</th>
                <th>Time</th>
                <th>Status</th>
              </tr>
              {data.pickupTimes
                .filter(
                  (p) =>
                    (p.dropLocation || p.location) === selectedLocation &&
                    (!selectedDate || p.date === selectedDate)
                )
                .map((location) => {
                  const { open, dropLocation, time, pickupId, date } = location;

                  return (
                    <tr
                      className={open ? "openSlot" : "fullSlot"}
                      key={
                        pickupId + (dropLocation || location.location) + time
                      }
                    >
                      <td>{dropLocation || location.location}</td>
                      <td>
                        {date}, {time}
                      </td>
                      <td>{open ? "Open" : "Full"}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          {selectedLocation &&
            data.dropLocations &&
            (() => {
              const selectedDropLocation = data.dropLocations.find(
                (loc) => loc.name === selectedLocation
              );

              if (
                !selectedDropLocation ||
                !selectedDropLocation.futureDelivery
              ) {
                return null;
              }

              const formattedDate = formatDate(
                selectedDropLocation.futureDelivery
              );
              const ordersOpenDate = formatDateMonthDay(
                selectedDropLocation.futureDeliveryOpens
              );

              return (
                <div className="future-delivery-info">
                  <p>
                    <strong>
                      Future {selectedDropLocation.name} Pick-ups:
                    </strong>{" "}
                    Orders for {formattedDate} pick-up open on {ordersOpenDate}.
                  </p>
                </div>
              );
            })()}
        </div>
      )}
      {error && <p>{error}</p>}
    </div>
  );
};

export default PickupTimes;
